// Position keys use only [0-9a-z] (Lex and fractional.ts), where Postgres' collation,
// musl's and code-unit order all agree; localeCompare would follow the device locale.
export function compareKeys(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function compareKeysNullsLast(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return compareKeys(a, b);
}

export function byPosition(a: { position: string }, b: { position: string }): number {
  return compareKeys(a.position, b.position);
}

function instant(timestamp: string | null | undefined): number {
  const ms = timestamp ? Date.parse(timestamp) : NaN;
  return Number.isNaN(ms) ? -Infinity : ms;
}

// Mirrors `.order(completed_at: :desc)`. Timestamps may carry different offsets, so
// instants are compared; a cascade shares one instant, and position keeps it stable.
export function byCompletedAtDesc(
  a: { completedAt?: string | null; position: string },
  b: { completedAt?: string | null; position: string },
): number {
  return instant(b.completedAt) - instant(a.completedAt) || byPosition(a, b);
}

// The calendar date of an instant in an IANA zone, as YYYY-MM-DD.
export function dateInZone(timestamp: string, zone: string): string {
  const parts = zoneFormatter(zone).formatToParts(new Date(timestamp));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(zone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" });
    formatters.set(zone, formatter);
  }
  return formatter;
}
